import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { FloatingChatProvider } from './components/FloatingChatProvider';

// Pages
import HomePage from './pages/HomePage';
import LiveRoomPage from './pages/LiveRoomPage';
import DiscoverPage from './pages/DiscoverPage';
import ProfilePage from './pages/ProfilePage';
import ArtistDashboardPage from './pages/ArtistDashboardPage';
import ArtistSetupPage from './pages/ArtistSetupPage';
import TrackUploadPage from './pages/TrackUploadPage';
import PlaylistsPage from './pages/PlaylistsPage';
import AuthPage from './pages/AuthPage';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import SettingsPage from './pages/SettingsPage';
import EPKPage from './pages/EPKPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ScheduleReleasePage from './pages/ScheduleReleasePage';
import OwnerDashboardPage from './pages/OwnerDashboardPage';
import ClipsPage from './pages/ClipsPage';
import MessagesPage from './pages/MessagesPage';
import HelpCenterPage from './pages/HelpCenterPage';
import StoriesPage from './pages/StoriesPage';
import StoreSuccessPage from './pages/StoreSuccessPage';
import CartPage from './pages/CartPage';
import StoreManagerPage from './pages/StoreManagerPage';
import MarketplacePage from './pages/MarketplacePage';
import EventsPage from './pages/EventsPage';
import ReleasesPage from './pages/ReleasesPage';
import WalletPage from './pages/WalletPage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';
import RoadmapPage from './pages/RoadmapPage';
import SingleClipPage from './pages/SingleClipPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotificationsPage from './pages/NotificationsPage';
import TrafficPage from './pages/TrafficPage';
import GenreHubPage from './pages/GenreHubPage';
import AnalyticsTracker from './components/AnalyticsTracker';

// Components
import MiniPlayer from './components/MiniPlayer';
import BottomNav from './components/BottomNav';
import { Toaster } from 'sonner';

// Pages where BottomNav should be hidden
const HIDE_NAV_PATHS = ['/auth', '/auth/callback', '/auth/google/success', '/artist-setup', '/upload', '/epk', '/analytics', '/schedule-release', '/settings', '/owner-dashboard', '/owner/traffic', '/messages', '/store', '/clip/', '/forgot-password', '/reset-password'];

// Pages that need overflow-hidden (for swipe/scroll-lock effects)
const OVERFLOW_HIDDEN_PATHS = ['/', '/room', '/clips', '/clip/'];

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  const hideNav = HIDE_NAV_PATHS.some(path => location.pathname.startsWith(path));
  const needsOverflowHidden = OVERFLOW_HIDDEN_PATHS.some(path => 
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <FloatingChatProvider>
      <AnalyticsTracker />
      <div className="fixed inset-0 bg-[#050505] flex flex-col">
        {/* Main Content */}
        <main className={`flex-1 ${needsOverflowHidden ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/room/:artistId" element={<LiveRoomPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/releases" element={<ReleasesPage />} />
            <Route path="/genre/:slug" element={<GenreHubPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/callback" element={<AuthPage />} />
            <Route path="/auth/google/success" element={<GoogleAuthCallback />} />
            
            {/* Protected Routes */}
            <Route 
              path="/profile" 
              element={isAuthenticated ? <ProfilePage /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/profile/:userId" 
              element={<ProfilePage />} 
            />
            <Route 
              path="/artist-dashboard" 
              element={isAuthenticated ? <ArtistDashboardPage /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/dashboard" 
              element={isAuthenticated ? <ArtistDashboardPage /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/artist-setup" 
              element={isAuthenticated ? <ArtistSetupPage /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/upload" 
              element={isAuthenticated ? <TrackUploadPage /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/playlists" 
              element={isAuthenticated ? <PlaylistsPage /> : <Navigate to="/auth" />} 
            />
            <Route 
              path="/settings" 
              element={isAuthenticated ? <SettingsPage /> : <Navigate to="/auth" />} 
            />
            
            {/* Public EPK Route */}
            <Route path="/epk/:stageName" element={<EPKPage />} />
            
            {/* Analytics Route */}
            <Route 
              path="/analytics" 
              element={isAuthenticated ? <AnalyticsPage /> : <Navigate to="/auth" />} 
            />
            
            {/* Schedule Release Route */}
            <Route 
              path="/schedule-release" 
              element={isAuthenticated ? <ScheduleReleasePage /> : <Navigate to="/auth" />} 
            />
            
            {/* Owner Dashboard Route */}
            <Route 
              path="/owner-dashboard" 
              element={isAuthenticated ? <OwnerDashboardPage /> : <Navigate to="/auth" />} 
            />

            {/* Owner Traffic / Visitor analytics */}
            <Route 
              path="/owner/traffic" 
              element={isAuthenticated ? <TrafficPage /> : <Navigate to="/auth" />} 
            />
            
            {/* Clips Page Route */}
            <Route path="/clips" element={<ClipsPage />} />
            
            {/* Help Center Route */}
            <Route path="/help" element={<HelpCenterPage />} />

            {/* Stories Route */}
            <Route path="/stories" element={<StoriesPage />} />

            {/* Store checkout return Route */}
            <Route path="/store/success" element={<StoreSuccessPage />} />
            <Route path="/store/cart" element={<CartPage />} />

            {/* Artist Store Manager */}
            <Route
              path="/store-manager"
              element={isAuthenticated ? <StoreManagerPage /> : <Navigate to="/auth" />}
            />

            {/* Unified Marketplace (public) */}
            <Route path="/marketplace" element={<MarketplacePage />} />

            {/* Public info pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/acceptable-use" element={<TermsPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />

            {/* Password reset (public) */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Single shareable clip (public) */}
            <Route path="/clip/:clipId" element={<SingleClipPage />} />
            
            {/* Messages Route */}
            <Route 
              path="/messages" 
              element={isAuthenticated ? <MessagesPage /> : <Navigate to="/auth" />} 
            />

            {/* Notifications Route */}
            <Route
              path="/notifications"
              element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/auth" />}
            />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* Mini Player - Shows when track is playing */}
        <MiniPlayer />

        {/* Bottom Navigation - Hidden on certain pages */}
        {!hideNav && <BottomNav />}

        {/* Toast notifications */}
        <Toaster position="top-center" theme="dark" richColors />
      </div>
    </FloatingChatProvider>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
